import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Building2, Crown } from 'lucide-react';
import apiClient from '../api/client';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import TopAlert from '../components/TopAlert';
import { getCurrencySymbol } from '../utils/currency';

const Pricing = () => {
  const { user, checkAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [pageAlert, setPageAlert] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await apiClient.get('/subscriptions/plans');
        setPlans(response.data);
        setPageAlert('');
      } catch (err) {
        console.error('Failed to fetch plans', err);
        setPageAlert('Failed to load pricing plans. Please try again.');
        toast.error('Failed to load pricing plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    try {
      const response = await apiClient.post(`/subscriptions/subscribe?plan_id=${planId}`);
      const data = response.data;

      if (data.provider === 'razorpay') {
        const res = await loadRazorpay();
        if (!res) {
          toast.error('Razorpay SDK failed to load');
          return;
        }

        const options = {
          key: data.key_id,
          subscription_id: data.subscription_id,
          name: data.name,
          description: data.description,
          handler: async function (response) {
            try {
                await apiClient.post('/subscriptions/verify-razorpay', {
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_subscription_id: response.razorpay_subscription_id,
                    razorpay_signature: response.razorpay_signature,
                    plan_id: planId
                });
                toast.success('Subscription active!');
                await checkAuth();
                navigate('/dashboard');
            } catch (err) {
                console.error(err);
                toast.error('Payment verification failed');
            }
          },
          prefill: data.prefill,
          theme: { color: '#6366f1' }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();

      } else if (data.checkout_url) {
        // Stripe fallback
        window.location.href = data.checkout_url;
      } else {
        // Free plan
        toast.success(data.message || 'Subscribed!');
        await checkAuth();
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Subscription failed', err);
      toast.error('Failed to initiate subscription');
    } finally {
      setSubscribing(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'trial': return <Check className="w-6 h-6 text-slate-500" />;
      case 'solo': return <Zap className="w-6 h-6 text-indigo-500" />;
      case 'pro': return <Crown className="w-6 h-6 text-amber-500" />;
      case 'studio': return <Building2 className="w-6 h-6 text-emerald-500" />;
      default: return <Zap className="w-6 h-6 text-indigo-500" />;
    }
  };

  if (loading) return <div className="p-8 text-center italic text-slate-500">Loading plans...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="mb-6">
        <TopAlert message={pageAlert} tone="error" onClose={() => setPageAlert('')} />
      </div>
      <div className="text-center mb-16">
        <h1 className="text-4xl font-black text-slate-900 mb-4">Choose Your Path to Freedom</h1>
        <p className="text-xl text-slate-500">Simple, transparent pricing for freelancers and companies of all sizes.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <div key={plan.plan_id} className={`bg-white rounded-3xl border ${plan.plan_id === 'pro' ? 'border-indigo-500 ring-4 ring-indigo-50' : 'border-slate-200'} p-6 shadow-sm flex flex-col relative overflow-hidden`}>
            {plan.plan_id === 'pro' && (
              <div className="absolute top-0 right-0 bg-indigo-500 text-white px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-bl-xl">
                Popular
              </div>
            )}
            
            {plan.plan_id === 'trial' && (
               <div className="absolute top-0 right-0 bg-slate-100 text-slate-600 px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-bl-xl">
                 Trial
               </div>
            )}

            <div className="mb-6">
              <div className="mb-4">{getPlanIcon(plan.plan_id)}</div>
              <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
              <p className="text-slate-500 text-xs mt-1 h-10">{plan.description}</p>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline">
                <span className="text-3xl font-black text-slate-900">{getCurrencySymbol(user?.currency)}{plan.price}</span>
                <span className="text-slate-500 ml-1 text-sm">/{plan.interval}</span>
              </div>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start">
                  <Check className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600 text-xs">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSubscribe(plan.plan_id)}
              disabled={subscribing !== null}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
                plan.plan_id === 'pro'
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-100'
                  : plan.plan_id === 'trial' ? 'bg-slate-800 text-white hover:bg-slate-900' : 'bg-slate-50 text-slate-900 hover:bg-slate-100'
              } disabled:opacity-50`}
            >
              {subscribing === plan.plan_id ? 'Processing...' : plan.price === 0 ? (plan.plan_id === 'trial' ? 'Start Trial' : 'Get Started') : 'Upgrade Now'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Pricing;
